// src/services/storageService.js - FIXED to handle both File uploads and avatar URLs
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, db } from "../firebase";
import { doc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";

/**
 * FIXED: Handle both File uploads and pre-made avatar URL selection
 * @param {string} userId - User ID
 * @param {File|string} fileOrUrl - Either a File object (for uploads) or URL string (for pre-made avatars)
 * @returns {Promise<string>} - The avatar URL
 */
const uploadProfilePhoto = async (userId, fileOrUrl) => {
  if (!fileOrUrl) {
    throw new Error("No file or URL provided for avatar update.");
  }

  let downloadURL;

  // Check if it's a File object (actual upload), string (pre-made avatar URL), or avatar object
  if (fileOrUrl instanceof File) {
    // Handle actual file upload
    console.log('Uploading new file:', fileOrUrl.name);

    // Create a unique filename with timestamp to prevent caching issues
    const timestamp = Date.now();
    const fileExtension = fileOrUrl.name.split('.').pop();
    const fileName = `avatar_${timestamp}.${fileExtension}`;

    // Create a storage reference: 'profile_photos/USER_ID/avatar_timestamp.jpg'
    const storageRef = ref(storage, `profile_photos/${userId}/${fileName}`);

    try {
      // Upload the file
      const snapshot = await uploadBytes(storageRef, fileOrUrl);
      console.log('Uploaded profile photo:', snapshot);

      // Get the public URL of the uploaded file
      downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Profile photo URL:', downloadURL);
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      throw error;
    }

  } else if (typeof fileOrUrl === 'string') {
    // Handle pre-made avatar URL selection (direct string)
    console.log('Setting pre-made avatar URL:', fileOrUrl);
    downloadURL = fileOrUrl;

    // Validate that it's a reasonable URL
    if (!downloadURL.startsWith('http') && !downloadURL.startsWith('/')) {
      throw new Error("Invalid avatar URL provided.");
    }
  } else if (typeof fileOrUrl === 'object' && fileOrUrl?.isAvatarUrl && fileOrUrl?.url) {
    // Handle avatar object from AvatarPicker
    console.log('Setting pre-made avatar from object:', fileOrUrl.url);
    downloadURL = fileOrUrl.url.trim(); // Remove any trailing spaces

    // Validate that it's a reasonable URL
    if (!downloadURL.startsWith('http') && !downloadURL.startsWith('/')) {
      throw new Error("Invalid avatar URL provided.");
    }
  } else {
    // Enhanced error message with debugging info
    throw new Error(`Invalid input: expected File object, URL string, or avatar object. Received: ${typeof fileOrUrl} (${fileOrUrl?.constructor?.name}). Value: ${JSON.stringify(fileOrUrl)}`);
  }

  try {
    // Update Firestore user document with the new photo URL
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      photoURL: downloadURL,
      avatarUrl: downloadURL, // Redundancy for backward compatibility
      updatedAt: serverTimestamp()
    });

    console.log('Updated Firestore user document with photo URL');
    return downloadURL;
  } catch (error) {
    console.error("Error updating user document with photo URL:", error);
    throw error;
  }
};

/**
 * Get profile photo URL from Firestore (with fallback to generate default)
 * @param {string} userId - User ID
 * @returns {Promise<string>} - Photo URL or default avatar
 */
const getProfilePhotoURL = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.photoURL) {
        return userData.photoURL;
      }
    }

    // Return null if no photo URL found
    return null;
  } catch (error) {
    console.error("Error getting profile photo URL:", error);
    return null;
  }
};

/**
 * Get audio file URL from Firebase Storage
 * @param {string} audioPath - Path to audio file (e.g., 'audio/goethe-c1/listening-part-1.mp3')
 * @returns {Promise<string>} - Download URL for the audio file
 */
const getAudioUrl = async (audioPath) => {
  if (!audioPath) {
    throw new Error("No audio path provided.");
  }

  try {
    const audioRef = ref(storage, audioPath);
    const downloadURL = await getDownloadURL(audioRef);
    console.log('Audio URL retrieved:', downloadURL);
    return downloadURL;
  } catch (error) {
    console.error("Error retrieving audio URL:", error);
    // Return null instead of throwing to allow graceful handling
    return null;
  }
};

/**
 * Upload audio file to Firebase Storage
 * @param {string} examProvider - Exam provider (e.g., 'goethe', 'telc', 'osd')
 * @param {string} level - Level (e.g., 'b1', 'b2', 'c1')
 * @param {File} file - Audio file to upload
 * @returns {Promise<string>} - Download URL for uploaded audio
 */
const uploadAudioFile = async (examProvider, level, file) => {
  if (!file) {
    throw new Error("No audio file provided for upload.");
  }

  // Validate file type
  const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg'];
  if (!validTypes.includes(file.type)) {
    throw new Error("Invalid audio file type. Supported types: MP3, WAV, OGG");
  }

  // Create storage path: 'audio/PROVIDER-LEVEL/listening-section.mp3'
  const fileExtension = file.name.split('.').pop();
  const fileName = `listening-section-${Date.now()}.${fileExtension}`;
  const audioPath = `audio/${examProvider}-${level}/${fileName}`;

  const storageRef = ref(storage, audioPath);

  try {
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);
    console.log('Uploaded audio file:', snapshot);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Audio file URL:', downloadURL);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading audio file:", error);
    throw error;
  }
};
/**
 * Generate audio path for listening sections
 * @param {string} examProvider - Exam provider (e.g., 'Goethe', 'TELC', 'OSD')
 * @param {string} level - Level (e.g., 'B1', 'B2', 'C1')
 * @param {string} sectionId - Section ID (should be 'listening' for audio)
 * @param {string} examFileName - Optional: The exam JSON filename (e.g., 'telc-deutsch-b1-mock-001 - Claude 1.json')
 * @param {string} variantName - Optional: The variant name (e.g., 'Übungstest Eins')
 * @returns {string|null} - Storage path for audio file or null
 */
const generateAudioPath = (examProvider, level, sectionId, examFileName = null, variantName = null) => {
  // Convert to lowercase for consistency
  const provider = examProvider.toLowerCase();
  const lvl = level.toLowerCase();

  // Only generate paths for listening sections
  if (!sectionId.includes('listening')) {
    return null;
  }

  // Determine audio filename based on exam variant
  // Works for all levels (B1, B2, C1) and all providers (TELC, Goethe, OSD)
  let audioFileName;
  
  // Check if this is the "Übungstest" (original) exam - use listening-section.mp3
  // This covers: "Übungstest Eins" (TELC), "original" (Goethe/OSD), or base filename without variant suffix
  // Examples: 
  //   - telc-deutsch-b1-mock-001.json -> listening-section.mp3
  //   - telc-deutsch-b2-mock-001.json -> listening-section.mp3
  const isOriginalExam = variantName && (
    variantName.toLowerCase().includes('übungstest eins') || 
    variantName.toLowerCase().includes('ubungstest eins') ||
    variantName.toLowerCase().includes('original')
  ) || (examFileName && !examFileName.includes(' - ')); // Base filename without variant suffix
  
  if (isOriginalExam) {
    audioFileName = 'listening-section.mp3';
  } else if (examFileName) {
    // For variant exams, derive audio filename from JSON filename
    // Examples:
    //   - "telc-deutsch-b1-mock-001 - Claude 1.json" -> "telc-deutsch-b1-mock-001 - Claude 1.mp3"
    //   - "telc-deutsch-b2-mock-001 - Claude 1.json" -> "telc-deutsch-b2-mock-001 - Claude 1.mp3"
    audioFileName = examFileName.replace(/\.json$/i, '.mp3');
  } else {
    // Fallback to default if no filename provided
    audioFileName = 'listening-section.mp3';
  }

  return `audio/${provider}-${lvl}/${audioFileName}`;
};

/**
 * Check if audio file exists in storage
 * @param {string} audioPath - Path to check
 * @returns {Promise<boolean>} - True if file exists
 */
const audioFileExists = async (audioPath) => {
  if (!audioPath) return false;
  try {
    await getDownloadURL(ref(storage, audioPath));
    return true;
  } catch (error) {
    // Error code 'storage/object-not-found' means the file doesn't exist
    if (error.code === 'storage/object-not-found') {
      return false;
    }
    // For other errors, log them but still return false
    console.error("Error checking if audio file exists:", error);
    return false;
  }
};

// Export all functions as default object
export default {
  uploadProfilePhoto,
  getProfilePhotoURL,
  getAudioUrl,
  uploadAudioFile,
  generateAudioPath,
  audioFileExists,
};